import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Access links from Supabase land here with #access_token=… in the URL.
// We parse the fragment ourselves, write the session into the same localStorage
// key supabase-js reads from on next load, then HARD-RELOAD onto the homepage.
//
// Why hard-reload instead of supabase.auth.setSession + navigate(): in v2.105
// setSession leaves the GoTrueClient in a state where subsequent getSession()
// / from() calls hang forever. Loading the session from storage on a fresh
// page load works reliably, so we just route through that path.
export default function AuthCallback() {
  const { t } = useTranslation();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    const expires_at = parseInt(params.get('expires_at') ?? '0', 10);
    const expires_in = parseInt(params.get('expires_in') ?? '0', 10);
    const token_type = params.get('token_type') ?? 'bearer';
    const errParam = params.get('error_description') || params.get('error');

    if (errParam) {
      setError(errParam);
      return;
    }

    if (!access_token || !refresh_token) {
      window.location.replace('/auth');
      return;
    }

    try {
      // Decode the JWT payload to extract user info — supabase-js's storage
      // format expects { user, access_token, refresh_token, ... }.
      const payload = JSON.parse(
        atob(access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
      );
      const session = {
        access_token,
        refresh_token,
        expires_at,
        expires_in,
        token_type,
        user: {
          id: payload.sub,
          email: payload.email,
          aud: payload.aud,
          role: payload.role,
          app_metadata: payload.app_metadata ?? {},
          user_metadata: payload.user_metadata ?? {},
        },
      };
      localStorage.setItem('hc.auth', JSON.stringify(session));
      // Hard navigation — full page reload onto the homepage so supabase-js
      // boots with the session already in storage. Profile lives in the user
      // menu for those who want it.
      window.location.replace('/');
    } catch (e) {
      setError(e.message ?? String(e));
    }
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-md px-8 pb-20 pt-32 text-center">
        <h1 className="font-serif text-3xl text-gold-light">{t('common.error')}</h1>
        <p className="mt-3 text-sm text-ink-secondary">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-8 pb-20 pt-32 text-center">
      <h1 className="font-serif text-3xl text-gold-light">{t('auth.callback.title')}</h1>
      <p className="mt-3 text-sm text-ink-secondary">{t('auth.callback.body')}</p>
    </div>
  );
}
